import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { Job } from '../../jobc';
import { JobService } from '../../services/job/job.service';
import { environment } from '../../../../../environments/environment';
import { FindingService } from '../../services/finding/finding.service';
import { showPdfResult, showPdfError } from '../../../../services/pdf-result';

type DocKey = 'wdo' | 'contract';

interface ReviewDoc {
  key: DocKey;
  title: string;
  badge: string;
  openLabel: string;
  fileField: string;
  reviewedField: string;
  // findinggenerateReport type / gen_type
  type: string;
  genType: string;
  check: string;
}

// Mockup confirm-inspection (2026-10-23): the WDO report and the work authorization contract.
const DOCS: ReviewDoc[] = [
  { key: 'wdo', title: 'Findings Report (WDO)', badge: 'WDO', openLabel: 'Open WDO', fileField: 'report_pdf_file',
    reviewedField: 'report_pdf_reviewed_file', type: 'finding_only', genType: '', check: 'I reviewed the WDO report and it is correct.' },
  { key: 'contract', title: 'Work Authorization Contract', badge: 'DOC', openLabel: 'Open Contract', fileField: 'report_pdf_file_w_contract',
    reviewedField: 'report_contract_reviewed_file', type: 'generate_contract', genType: 'new', check: 'I reviewed the contract and it is correct.' },
];

/**
 * Confirm Inspection: Review Documents & Sign Off (mockup 2026-10-23).
 *
 * Each document is generated, opened (preview dialog), then marked correct; the mark is stored as
 * the reviewed PDF's file name (api/inspection-document-review), so regenerating it - here or on
 * any other page - needs a new review. Sign-off needs both reviews and the certification; the
 * API checks the reviews again and records who signed off.
 */
@Component({
  selector: 'app-confirm-inspection',
  templateUrl: './confirm-inspection.component.html',
  styleUrls: ['./confirm-inspection.component.scss']
})
export class ConfirmInspectionComponent implements OnInit {
  page_title = ' Inspection: Review/Finalizing';
  readonly docs = DOCS;

  jobId: number;
  loading = true;
  status = '';
  report: any = {};
  jobData: any = {};
  job = new Job(null, '', null, null, null, null, null, null, null,
                null, '', null, '', '', '', '', null, '', null, '', '', '', '', '', '', '', '', '');

  generating: DocKey = null;
  saving: DocKey = null;
  opened: { [key: string]: boolean } = {};
  certified = false;
  signingOff = false;
  feedback = '';
  preview: ReviewDoc = null;
  previewUrl: SafeResourceUrl = null;

  private doSpace = environment.doSpace;

  constructor(
    private jobService: JobService,
    private findingService: FindingService,
    private sanitizer: DomSanitizer,
    private router: ActivatedRoute) { }

  ngOnInit() {
    this.router.params.subscribe(params => {
      if (params.jobId) {
        this.jobId = params.jobId;
        this.getJob(params.jobId);
      }
    });
  }

  getJob(jid) {
    this.jobService.getJobConfirm(jid).subscribe(data => {
      this.loading = false;
      if (!data) {
        return;
      }
      this.jobData = data;
      this.job = new Job(
        data.job_id, data.job_ref_suffix, data.job_ref_parent, data.client_id, data.billing_id,
        data.buyer_agent_id, data.seller_agent_id, data.title_escrow_id, data.property_id, data.agent_id,
        data.job_sub_type, data.scheduled_id, data.job_date_scheduled, data.job_fee, data.job_fee_discount,
        data.job_fee_discount_type,
        data.report ? data.report.report_id : '', data.job_status,
        data.report ? data.report.report_change_status : '',
        data.report ? data.report.report_description_general : '',
        data.report ? data.report.report_diagram_file : '',
        data.report ? data.report.report_pdf_file : null,
        data.report ? data.report.report_pdf_file_w_contract : null,
        data.job_property ? data.job_property.property_address1 : '',
        data.job_property ? data.job_property.property_address2 : '',
        data.job_property ? data.job_property.property_city : '',
        data.job_property ? data.job_property.property_state : '',
        data.job_property ? data.job_property.property_zip : '',
      );
      if (!data.report) {
        this.status = 'NoReport';
        return;
      }
      this.report = data.report;
      // Signed off: Work or Completed, or any later status once the inspection has a completion date.
      const signedOff = ['Work', 'Completed'].indexOf(data.job_status) !== -1 || !!this.dateOrEmpty(data.job_date_inspection_completed);
      this.status = data.job_status === 'Inspection' ? 'Inspection' : signedOff ? 'Work' : 'Other';
    });
  }

  // ---- Documents ----
  file(doc: ReviewDoc): string {
    return this.report[doc.fileField] || '';
  }

  isReviewed(doc: ReviewDoc): boolean {
    return !!this.file(doc) && this.report[doc.reviewedField] === this.file(doc);
  }

  // A document can be marked correct once it has been opened (or was already reviewed).
  canMark(doc: ReviewDoc): boolean {
    return !!this.file(doc) && (this.opened[doc.key] || this.isReviewed(doc)) && !this.saving;
  }

  state(doc: ReviewDoc): string {
    return !this.file(doc) ? 'missing' : this.isReviewed(doc) ? 'reviewed' : 'generated';
  }

  stateLabel(doc: ReviewDoc): string {
    // Signed off before reviews were recorded: the documents are simply available.
    const generated = this.status === 'Work' ? 'Available' : 'Ready to review';
    return { missing: 'Not generated', generated, reviewed: 'Reviewed' }[this.state(doc)];
  }

  // No hint until the document has been opened (client, 2026-11-03).
  guidance(doc: ReviewDoc): string {
    if (this.isReviewed(doc)) {
      return 'Marked as correct.';
    }
    return this.file(doc) && this.opened[doc.key] ? 'Mark this document as correct after reviewing it.' : '';
  }

  get reviewedCount(): number {
    return this.docs.filter(d => this.isReviewed(d)).length;
  }

  get canSignOff(): boolean {
    return this.reviewedCount === this.docs.length && this.certified && !this.signingOff;
  }

  generate(doc: ReviewDoc) {
    this.generating = doc.key;
    this.feedback = '';
    this.findingService.generateReport({ type: doc.type, jid: this.jobId, gen_type: doc.genType }).subscribe(
      data => {
        this.generating = null;
        // A document with warnings still exists, so refresh either way. The new file name no
        // longer matches its review, so it (and the certification) needs doing again.
        if (showPdfResult(data)) {
          this.opened[doc.key] = false;
          this.certified = false;
          this.getJob(this.jobId);
        }
      },
      error => {
        this.generating = null;
        showPdfError(error);
      }
    );
  }

  open(doc: ReviewDoc) {
    if (!this.file(doc)) {
      return;
    }
    this.preview = doc;
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.docUrl(doc));
    this.opened[doc.key] = true;
  }

  docUrl(doc: ReviewDoc): string {
    return this.doSpace + '/media/reports/' + this.jobId + '/' + this.file(doc);
  }

  @HostListener('document:keydown.escape')
  closePreview() {
    this.preview = null;
    this.previewUrl = null;
  }

  setReviewed(doc: ReviewDoc, reviewed: boolean) {
    if (reviewed && !this.canMark(doc)) {
      return;
    }
    this.saving = doc.key;
    this.feedback = '';
    this.jobService.reviewDocument(this.jobId, doc.key, reviewed).subscribe(
      res => {
        this.saving = null;
        const d = (res && res.data) || {};
        this.report.report_pdf_reviewed_file = d.report_pdf_reviewed_file;
        this.report.report_contract_reviewed_file = d.report_contract_reviewed_file;
        if (!reviewed) {
          this.certified = false;
        }
      },
      err => {
        this.saving = null;
        this.feedback = (err && err.error && err.error.message) || "Couldn't save the review. Try again.";
      }
    );
  }

  markFromPreview() {
    const doc = this.preview;
    this.closePreview();
    if (doc && !this.isReviewed(doc)) {
      this.setReviewed(doc, true);
    }
  }

  // ---- Sign-off ----
  signOff() {
    if (!this.canSignOff) {
      return;
    }
    this.signingOff = true;
    this.feedback = '';
    this.jobService.addInspectionCompleted(this.job).subscribe(
      () => {
        // Stay here: the page reloads into the completed banner (mockup).
        this.signingOff = false;
        this.certified = false;
        this.getJob(this.jobId);
      },
      err => {
        this.signingOff = false;
        this.feedback = (err && err.error && err.error.message) || "Couldn't complete the inspection. Try again.";
      }
    );
  }

  get signedOffBy(): string {
    const u = this.jobData.inspection_completed_by;
    return u ? [u.user_first_name, u.user_last_name].filter(Boolean).join(' ') : '';
  }

  get signedOffAt(): string {
    return this.dateOrEmpty(this.jobData.job_date_inspection_completed);
  }

  private dateOrEmpty(value): string {
    return value && String(value).indexOf('0000-00-00') !== 0 ? String(value).replace(' ', 'T') : '';
  }
}
